import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ExternalLink } from "lucide-react";

export default function ListPage({ collection, titleKey, columns, actions }) {
  return null;
}

/** Generic list helper */
export function useEmps() {
  const [m, setM] = useState({});
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/employees");
      const o = {}; data.forEach(e => o[e.id] = e); setM(o);
    })();
  }, []);
  return m;
}

export function EmpCell({ emp }) {
  if (!emp) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={`/employees/${emp.id}`} className="hover:text-primary inline-flex items-center gap-1">
      {emp.first_name} {emp.last_name}
      <ExternalLink className="w-3 h-3 opacity-60" />
    </Link>
  );
}

export function ContractsListPage() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const emps = useEmps();
  useEffect(() => { api.get("/contracts").then(r => setRows(r.data)); }, []);
  return (
    <Card className="p-5 border-border/60" data-testid="contracts-page">
      <h3 className="font-display font-bold mb-3">{t("contracts")}</h3>
      {rows.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">{t("noData")}</div> : (
        <div className="divide-y divide-border/60">
          {rows.map((r, i) => (
            <div key={r.id} className="py-3 flex items-center justify-between gap-3">
              <div>
                <div className="font-medium text-sm"><EmpCell emp={emps[r.employee_id]} /></div>
                <div className="text-xs text-muted-foreground font-mono-brand">
                  {r.contract_type} · {r.start_date}{r.end_date ? ` → ${r.end_date}` : ""} · {r.gross_salary} {r.currency}
                </div>
              </div>
              <Badge variant={r.status === "active" ? "default" : "secondary"}>{r.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function PayrollListPage() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const emps = useEmps();
  const refresh = () => api.get("/payroll").then(r => setRows(r.data));
  useEffect(() => { refresh(); }, []);
  const total = rows.reduce((s, r) => s + (r.net_salary || 0), 0);
  return (
    <Card className="p-5 border-border/60" data-testid="payroll-page">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold">{t("payroll")}</h3>
        <div className="text-sm text-muted-foreground font-mono-brand">
          Total: <span className="font-bold text-foreground">{total.toFixed(2)} TRY</span>
        </div>
      </div>
      {rows.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">{t("noData")}</div> : (
        <div className="divide-y divide-border/60">
          {rows.map((r, i) => (
            <div key={r.id} className="py-3 flex items-center justify-between gap-3">
              <div>
                <div className="font-medium text-sm"><EmpCell emp={emps[r.employee_id]} /></div>
                <div className="text-xs text-muted-foreground font-mono-brand">
                  {r.period} · {t("netSalary")}: {r.net_salary?.toFixed(2)} {r.currency}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={r.paid ? "default" : "secondary"}>{r.paid ? t("paid") : t("pending")}</Badge>
                {!r.paid && <Button size="sm" variant="outline" onClick={async () => { await api.post(`/payroll/${r.id}/pay`); refresh(); }}>{t("markPaid")}</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function LeavesListPage() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const emps = useEmps();
  const refresh = () => api.get("/leaves").then(r => setRows(r.data));
  useEffect(() => { refresh(); }, []);
  return (
    <Card className="p-5 border-border/60" data-testid="leaves-page">
      <h3 className="font-display font-bold mb-3">{t("leaves")}</h3>
      {rows.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">{t("noData")}</div> : (
        <div className="divide-y divide-border/60">
          {rows.map((r, i) => (
            <div key={r.id} className="py-3 flex items-center justify-between">
              <div>
                <div className="font-medium text-sm"><EmpCell emp={emps[r.employee_id]} /></div>
                <div className="text-xs text-muted-foreground font-mono-brand">{t(r.leave_type)} · {r.start_date} → {r.end_date} · {r.days}d</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={r.status === "approved" ? "default" : r.status === "rejected" ? "destructive" : "secondary"}>{t(r.status)}</Badge>
                {r.status === "pending" && (
                  <>
                    <Button data-testid={`list-approve-${i}`} size="sm" variant="outline" onClick={async () => { await api.post(`/leaves/${r.id}/approve`); refresh(); }}>{t("approve")}</Button>
                    <Button data-testid={`list-reject-${i}`} size="sm" variant="outline" onClick={async () => { await api.post(`/leaves/${r.id}/reject`); refresh(); }}>{t("reject")}</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function TrainingsListPage() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const emps = useEmps();
  useEffect(() => { api.get("/trainings").then(r => setRows(r.data)); }, []);
  return (
    <Card className="p-5 border-border/60" data-testid="trainings-page">
      <h3 className="font-display font-bold mb-3">{t("trainings")}</h3>
      {rows.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">{t("noData")}</div> : (
        <div className="divide-y divide-border/60">
          {rows.map((r) => (
            <div key={r.id} className="py-3">
              <div className="font-medium text-sm"><EmpCell emp={emps[r.employee_id]} /> · {r.title}</div>
              <div className="text-xs text-muted-foreground font-mono-brand">{r.category} · {r.provider || "—"} · {r.start_date}{r.expiry ? ` · ${t("expiryDate")}: ${r.expiry}` : ""}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function PerformanceListPage() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const emps = useEmps();
  useEffect(() => { api.get("/performance").then(r => setRows(r.data)); }, []);
  return (
    <Card className="p-5 border-border/60" data-testid="performance-page">
      <h3 className="font-display font-bold mb-3">{t("performance")}</h3>
      {rows.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">{t("noData")}</div> : (
        <div className="divide-y divide-border/60">
          {rows.map((r) => (
            <div key={r.id} className="py-3 flex items-center justify-between">
              <div>
                <div className="font-medium text-sm"><EmpCell emp={emps[r.employee_id]} /></div>
                <div className="text-xs text-muted-foreground font-mono-brand">{r.period} · {r.reviewer}</div>
              </div>
              <Badge>{r.overall_score}/5</Badge>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function AssetsListPage() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const emps = useEmps();
  const refresh = () => api.get("/assets").then(r => setRows(r.data));
  useEffect(() => { refresh(); }, []);
  return (
    <Card className="p-5 border-border/60" data-testid="assets-page">
      <h3 className="font-display font-bold mb-3">{t("assets")}</h3>
      {rows.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">{t("noData")}</div> : (
        <div className="divide-y divide-border/60">
          {rows.map((r) => (
            <div key={r.id} className="py-3 flex items-center justify-between">
              <div>
                <div className="font-medium text-sm"><EmpCell emp={emps[r.employee_id]} /> · {r.asset_name}</div>
                <div className="text-xs text-muted-foreground font-mono-brand">{t(r.asset_type)} · {r.serial_or_plate || "—"} · {r.assigned_date}</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={r.status === "assigned" ? "default" : "secondary"}>{r.status}</Badge>
                {r.status === "assigned" && (
                  <Button size="sm" variant="outline" onClick={async () => { await api.post(`/assets/${r.id}/return`); refresh(); }}>{t("returnAsset")}</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function DisciplineListPage() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const emps = useEmps();
  useEffect(() => { api.get("/discipline").then(r => setRows(r.data)); }, []);
  return (
    <Card className="p-5 border-border/60" data-testid="discipline-page">
      <h3 className="font-display font-bold mb-3">{t("discipline")}</h3>
      {rows.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">{t("noData")}</div> : (
        <div className="divide-y divide-border/60">
          {rows.map((r) => (
            <div key={r.id} className="py-3">
              <div className="flex items-center gap-2">
                <Badge variant="destructive">{r.category}</Badge>
                <span className="font-medium text-sm"><EmpCell emp={emps[r.employee_id]} /> · {r.reason}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 font-mono-brand">{r.incident_date} · {r.action_taken || "—"}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
